/**
 * §B7 soft enforcement — the nudge toward enrolment.
 *
 * `security.totpRequired` is true when the caller holds a permission the
 * server lists as TOTP-worthy and has not enrolled. v1 is deliberately soft:
 * the server does not yet block login, so the SPA's job is to make the gap
 * impossible to miss without making the product unusable. A full-width
 * `warning` band on the two screens everyone visits (Dashboard, Account) is
 * that instrument — `warning` because §11.6 refuses to let a tenant brand it,
 * so the nudge looks the same in every account.
 *
 * Renders nothing once enrolled, and nothing while `security` is still
 * unknown: a banner that flashes during boot teaches people to ignore it.
 */

import { ShieldAlert } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.js';

export function TotpRequiredBanner(): ReactElement | null {
  const { security } = useAuth();
  if (!security || !security.totpRequired || security.totpEnabled) return null;

  return (
    <div
      role="status"
      data-testid="totp-required-banner"
      className="mb-[var(--space-4)] flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-warning/20 bg-warning-soft px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-warning-text"
    >
      {/* The glyph is redundant with the colour on purpose (WCAG 1.4.1). */}
      <ShieldAlert aria-hidden="true" size={16} strokeWidth={2} className="mt-[2px] shrink-0" />
      <p>
        <strong className="font-semibold">Your role requires two-factor authentication.</strong>{' '}
        Set up an authenticator app to keep this account protected.{' '}
        <Link to="/account" className="font-medium underline underline-offset-2">
          Set it up now
        </Link>
      </p>
    </div>
  );
}
